import React, { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Box,
} from '@material-ui/core';
import {
  Title as TitleIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
} from '@material-ui/icons';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import { AppBar, ScreenLoader, Page, EmptyState, Amount } from 'components';
import { anketsStore, sdAnalyticsStore, backNavigationStore } from 'stores';
import { ru } from 'locale';
import { NoData as NoDataBg } from 'resources/images';
import useStyles from './SdAnalyticsScreen.styles';

const SdAnalyticsScreen = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { backUrl } = backNavigationStore;
  const { fetchState, analytics } = sdAnalyticsStore;

  useEffect(
    () =>
      autorun(() => {
        sdAnalyticsStore.getAnalytics(anketsStore.anketId);
      }),
    [],
  );

  const handleBackClick = () => {
    history.goBack();
  };

  const handleItemSelect = id => () => {
    history.push(`${path}/${id}`);
  };

  const renderAmount = value => {
    return (
      <Amount
        variant="h5"
        color="textPrimary"
        amount={{
          value,
          currency: { code: 'RUR', minority: 0 },
        }}
      />
    );
  };

  const renderProfit = debtor => {
    return (
      <div className={classes.profit}>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Typography component="span" color="textSecondary">{`${ru.revenue}: `}</Typography>
          </Grid>
          <Grid item>
            <Amount
              className={classes.revenue}
              variant="h6"
              amount={{ value: debtor.revenue, currency: { code: 'RUR', minority: 0 } }}
            />
          </Grid>
        </Grid>

        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Typography component="div" color="textSecondary">{`${ru.grossProfit}: `}</Typography>
          </Grid>
          <Grid item>
            <Amount
              color="textPrimary"
              variant="h6"
              amount={{ value: debtor.grossProfit, currency: { code: 'RUR', minority: 0 } }}
            />
          </Grid>
        </Grid>
      </div>
    );
  };

  const renderTotalRevenue = () => {
    return (
      <List component="nav" aria-label={ru.totalRevenue} className={classes.list}>
        <ListItem>
          <ListItemIcon>
            <TrendingUpIcon />
          </ListItemIcon>
          <ListItemText
            primary={ru.holdingRevenue}
            secondary={renderAmount(analytics.totalRevenue)}
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemIcon>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText
            primary={ru.holdingGrossProfit}
            secondary={renderAmount(analytics.totalGrossProfit)}
          />
        </ListItem>
      </List>
    );
  };

  const renderSubheader = () => {
    return (
      <ListSubheader component="div" id="nested-list-subheader">
        {ru.debtors}
      </ListSubheader>
    );
  };

  const renderEmptyState = () => {
    return (
      <EmptyState
        image={{ source: NoDataBg, alt: ru.sdAnalyticsEmpty }}
        title={ru.sdAnalyticsEmpty}
        position="fixed"
      />
    );
  };

  const renderItems = () => {
    const hasItems = analytics.debtors.length !== 0;

    if (!hasItems) {
      renderEmptyState();
    }

    return (
      <>
        {renderTotalRevenue()}

        <Box my={1}>
          <List
            component="nav"
            aria-label={ru.clients}
            className={classes.list}
            subheader={renderSubheader()}
          >
            {analytics.debtors.map(debtor => {
              return (
                // eslint-disable-next-line
                <Box my={1} key={debtor.id} onClick={handleItemSelect(debtor.id)}>
                  <ListItem button component="div">
                    <ListItemIcon>
                      <TitleIcon />
                    </ListItemIcon>
                    <ListItemText primary={debtor.name} secondary={debtor.salesOrganization} />
                  </ListItem>
                  <ListItem component="div">{renderProfit(debtor)}</ListItem>
                  <Divider />
                </Box>
              );
            })}
          </List>
        </Box>
      </>
    );
  };

  return (
    <Page fullScreen style={{ minHeight: '100rvh' }} className={classes.root}>
      <Grid container direction="column">
        <Grid item>
          <AppBar
            title={ru.sdAnalytics}
            onNavButtonClick={handleBackClick}
            backUrl={`${backUrl}#sd-analytics`}
            typeNavIcon="link"
          />

          {fetchState === 'pending' && <ScreenLoader />}
          {fetchState === 'fulfilled' && renderItems()}
          {fetchState === 'rejected' && renderEmptyState()}
        </Grid>
      </Grid>
    </Page>
  );
});

export default SdAnalyticsScreen;
